import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Loader from '../../common/Loader';
import { sendToastMessage } from '../../actions/toast';
import { closeFullScreenModalAction, closeModalAction, openCustomModal, openFullscreenModal, openMediaViewModal } from '../../actions/modal';
import IconToolTip from '../../common/IconToolTip';
import { getPavMilestoneAssets, getPavMilestoneLabel } from './pavConfig';
import PavMilestoneActions from './PavMilestoneActions';
import PavAssetCard from './PavAssetCard';
import PavImageUploader from './PavImageUploader';
import PavRaiseSignOffView from './PavRaiseSignOffView';
import SavedPavAssets from './SavedPavAssets';
import usePavAssetTypes from './usePavAssetTypes';
import usePavAttachments from './usePavAttachments';
import {
    applyAttachmentMoves,
    buildPavAssetPayload,
    createId,
    getMediaViewAttachments,
    getSavedAssetAttachments,
    getSavedAssetIdentity,
    groupAttachmentsIntoAssets,
    mergeSavedAssets,
} from './pavAssetUtils';
import { addBoqInServiceRequest, getServiceRequestById } from '../../actions/service_request';

const ChatHeader = ({ savedCount, assetCount, isSaving, isViewingSaved, onExpand, onViewSaved, onAddAsset }) => (
    <div className='flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-3 py-2'>
        <div className='flex min-w-0 items-center gap-2'>
            {isViewingSaved && (
                <button type='button' onClick={onViewSaved} aria-label='Back to asset verification' title='Back' className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-scogolightblue text-scogoprimary transition-colors hover:border-scogoprimary hover:bg-blue-100'>
                    <span className='material-icons text-font14'>arrow_back</span>
                </button>
            )}
            <span className='material-icons-outlined text-base text-scogoprimary'>verified</span>
            <div className='min-w-0'>
                <p className='truncate text-font11 font-medium text-scogo21'>{isViewingSaved ? 'Added assets' : 'Asset verification'}</p>
                <p className='truncate text-font10 font-normal text-slate-600'>{isViewingSaved ? `${savedCount} saved` : `${savedCount} saved, ${assetCount} detected`}</p>
            </div>
        </div>
        <div className='ml-2 flex shrink-0 items-center gap-1.5'>
            {savedCount > 0 && (
                <button type='button' disabled={isSaving} onClick={onViewSaved} className={`flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:border-emerald-400 hover:bg-emerald-100 ${isSaving ? 'cursor-not-allowed opacity-50' : ''}`} title={isViewingSaved ? 'Back to asset verification' : `View ${savedCount} saved asset${savedCount === 1 ? '' : 's'}`}>
                    <span className='material-icons-outlined text-lg'>inventory_2</span>
                </button>
            )}
            <button type='button' disabled={isSaving} onClick={onAddAsset} className={`flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 bg-white text-scogo21 transition-colors hover:border-scogoprimary hover:text-scogoprimary ${isSaving ? 'cursor-not-allowed opacity-50' : ''}`} title='Add an asset and fill its details manually'>
                <span className='material-icons text-lg'>add</span>
            </button>
            <button type='button' disabled={isSaving} onClick={onExpand} className={`flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 bg-scogolightblue text-scogoprimary transition-colors hover:border-scogoprimary hover:bg-blue-100 ${isSaving ? 'cursor-not-allowed opacity-50' : ''}`} title='Open full PAV form'>
                <span className='material-icons-outlined text-lg'>open_in_full</span>
            </button>
        </div>
    </div>
);

const FullScreenHeader = ({ milestone, ticketNumber, onClose, onShrink }) => (
    <div className='flex shrink-0 items-center gap-3.5 border-b border-scogoddd bg-white px-6 py-3'>
        <button
            type='button'
            onClick={onClose}
            className='flex items-center gap-0.5 rounded-md px-2 py-1 text-font13 font-semibold text-scogoprimary transition-colors hover:bg-scogolightblue hover:text-scogoorange'
            title='Back'
        >
            <span className='material-icons m-0 text-font18 font-bold'>chevron_left</span>
            <span className='m-0'>BACK</span>
        </button>
        <span className='h-4 w-px bg-slate-300' />
        <div className='flex min-w-0 items-center gap-1 truncate'>
            <h1 className='truncate text-font16 font-semibold text-black'>
                {getPavMilestoneLabel(milestone)} :&nbsp;
                {ticketNumber && <span className='text-font16 font-semibold text-scogobgsky'>{ticketNumber}</span>}
            </h1>
        </div>
        <div className='ml-auto flex shrink-0 items-center gap-2'>
            {onShrink && (
                <button
                    type='button'
                    onClick={onShrink}
                    className='flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-scogoprimary hover:bg-scogolightblue hover:text-scogoprimary'
                    title='Shrink to modal'
                >
                    <span className='material-icons-outlined text-font16'>close_fullscreen</span>
                </button>
            )}
        </div>
    </div>
);

const SummaryStat = ({ label, value, valueClass = 'text-scogo21' }) => (
    <span className='min-w-[3.25rem] px-3 py-1 text-center'>
        <strong className={`block text-font12 font-semibold leading-none ${valueClass}`}>{value}</strong>
        <span className='mt-0.5 block text-font09 font-normal leading-none text-slate-500'>{label}</span>
    </span>
);

// Single row: the icon and action cluster never shrink, and the description
// column takes the remaining width and wraps onto a second line when it needs to.
const FlowSummary = ({ imageCount, assetCount, readyCount, isProcessing, savedCount, isSaving, onViewSaved, onAddAsset, onExpand }) => (
    <div className='flex shrink-0 items-center gap-3.5 border-b border-slate-200 bg-white px-6 py-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.02)]'>
        <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-scogolightblue text-scogoprimary shadow-sm'>
            <span className='material-icons-outlined text-font16'>document_scanner</span>
        </span>
        <div className='min-w-0 flex-1 pr-3'>
            <p className='text-font12 font-semibold leading-tight text-scogo21'>Verify milestone assets</p>
            <p className='mt-0.5 text-font10 font-normal leading-4 text-slate-500'>Add photos, review only missing details, then save. Drag an image onto another card if it landed on the wrong asset.</p>
        </div>
        <div className='flex shrink-0 items-center gap-2'>
            <div className='flex divide-x divide-slate-200 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm'>
                <SummaryStat label='Images' value={imageCount} />
                <SummaryStat label='Assets' value={assetCount} />
                <SummaryStat label='Ready' value={isProcessing ? '...' : readyCount} valueClass={isProcessing ? 'text-scogobgsky' : 'text-emerald-600'} />
            </div>
            {savedCount > 0 && (
                <button type='button' disabled={isSaving} onClick={onViewSaved} className={`flex h-9 items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 text-font10 font-semibold text-emerald-700 shadow-sm transition-colors ${isSaving ? 'cursor-not-allowed opacity-50' : 'hover:border-emerald-400 hover:bg-emerald-100'}`} title='View the assets already added to this milestone'>
                    <span className='material-icons-outlined text-font14'>inventory_2</span>
                    {savedCount} saved
                </button>
            )}
            <button type='button' disabled={isSaving} onClick={onAddAsset} className={`flex h-9 items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 text-font11 font-medium text-scogo21 shadow-sm transition-colors ${isSaving ? 'cursor-not-allowed opacity-50' : 'hover:border-scogoprimary hover:text-scogoprimary'}`} title='Add an asset and fill its details manually'>
                <span className='material-icons text-font14'>add</span>
                Add asset
            </button>
            {onExpand && (
                <button type='button' disabled={isSaving} onClick={onExpand} className={`flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-scogolightblue text-scogoprimary shadow-sm transition-colors ${isSaving ? 'cursor-not-allowed opacity-50' : 'hover:border-scogoprimary hover:bg-blue-100'}`} title='Open this form on a full screen'>
                    <span className='material-icons-outlined text-font15'>open_in_full</span>
                </button>
            )}
        </div>
    </div>
);

export default function PavAssetVerification({ ticket, milestone, variant = 'modal', onSave, initialUpload = null, footerContent = null, reviewSavedAssetsOnly = false, onSignOffFormBack }) {
    const dispatch = useDispatch();
    const isChat = variant === 'chat';
    const isFullScreen = variant === 'fullscreen';
    const isChatRoute = useLocation().pathname.startsWith('/chat');
    const isCompact = isChat && !isChatRoute;
    const ticketId = ticket?.id || ticket?.ticket_id;
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const orgId = ticket?.org_id || newLoggedUserData?.org?.id;
    // One shared asset-type catalogue for every card, so concurrent lookups
    // cannot clobber each other's matches through the global redux slice.
    const assetTypes = usePavAssetTypes({ orgId });
    const refreshedTicket = useSelector((state) => state.serviceRequest.serviceRequestData?.[ticketId]);
    const refreshedMilestone = useMemo(
        () => refreshedTicket?.milestones?.find((item) => String(item.id) === String(milestone?.id)),
        [refreshedTicket, milestone?.id]
    );
    const currentTicket = useMemo(
        () => refreshedTicket ? { ...ticket, ...refreshedTicket } : ticket,
        [ticket, refreshedTicket]
    );
    const currentMilestone = useMemo(
        () => refreshedMilestone ? { ...milestone, ...refreshedMilestone } : milestone,
        [milestone, refreshedMilestone]
    );
    const ticketNumber = currentTicket?.ticket_number || currentTicket?.job_ticket_number || ticket?.ticket_number || ticket?.job_ticket_number;
    const scopeIdRef = useRef(createId('scope'));
    const cardRefs = useRef({});
    const milestoneSavedAssets = useMemo(() => {
        return mergeSavedAssets(
            getPavMilestoneAssets(ticket, milestone),
            getPavMilestoneAssets(currentTicket, currentMilestone)
        );
    }, [ticket, milestone, currentTicket, currentMilestone]);
    const [savedAssets, setSavedAssets] = useState(() => milestoneSavedAssets);
    const [hasSavedMilestoneAsset, setHasSavedMilestoneAsset] = useState(() => milestoneSavedAssets.length > 0);
    const [cardStatuses, setCardStatuses] = useState({});
    const [manualCardIds, setManualCardIds] = useState(() => initialUpload?.manualCardIds || []);
    const [attachmentMoves, setAttachmentMoves] = useState(() => initialUpload?.attachmentMoves || {});
    const [isSaving, setIsSaving] = useState(false);
    const [activeView, setActiveView] = useState(reviewSavedAssetsOnly ? 'saved' : 'assets');
    const [pendingScrollCardId, setPendingScrollCardId] = useState(null);
    const cardNodeRefs = useRef({});

    const {
        attachments,
        addFiles,
        removeAttachment,
        removeAttachments,
        getTransferAttachments,
        isProcessing,
        processedCount,
        hasFailedAttachment,
    } = usePavAttachments({
        ticketId,
        milestoneId: milestone?.id,
        scopeId: scopeIdRef.current,
        initialAttachments: initialUpload?.attachments || [],
    });

    // Detected cards are pure derived state: OCR results decide how many assets
    // exist, then the user's drag-and-drop corrections are replayed on top.
    // Manually added cards start empty and can receive dragged images too.
    const { groups: baseGroups } = useMemo(() => groupAttachmentsIntoAssets(attachments), [attachments]);
    const { detectedGroups, manualGroups } = useMemo(
        () => applyAttachmentMoves({ groups: baseGroups, manualCardIds, moves: attachmentMoves, attachments }),
        [baseGroups, manualCardIds, attachmentMoves, attachments]
    );
    const assetCards = useMemo(() => [...detectedGroups, ...manualGroups], [detectedGroups, manualGroups]);
    const firstEmptyAssetCardId = useMemo(
        () => assetCards.find((card) => card.attachments.length === 0 || card.canonicalFields.size === 0)?.id,
        [assetCards]
    );
    const assetIndexByAttachmentId = useMemo(() => {
        const indexById = new Map();
        assetCards.forEach((card, index) => card.attachments.forEach((attachment) => indexById.set(attachment.id, index)));
        return indexById;
    }, [assetCards]);

    useEffect(() => {
        if (milestoneSavedAssets.length === 0) return;
        setSavedAssets((current) => mergeSavedAssets(current, milestoneSavedAssets));
        setHasSavedMilestoneAsset(true);
    }, [milestoneSavedAssets]);

    // The saved assets carry their physical-verification state, which changes
    // outside this screen, so the added-assets view always opens on freshly
    // fetched ticket details instead of whatever the cache happened to hold.
    useEffect(() => {
        if (activeView !== 'saved' || !ticketId) return;
        dispatch(getServiceRequestById({ ticketId, callDetail: true }));
    }, [activeView, ticketId, dispatch]);

    // Drop statuses and refs of cards that no longer exist after a regroup.
    useEffect(() => {
        const cardIds = new Set(assetCards.map((card) => card.id));
        setCardStatuses((current) => {
            const entries = Object.entries(current).filter(([cardId]) => cardIds.has(cardId));
            return entries.length === Object.keys(current).length ? current : Object.fromEntries(entries);
        });
        Object.keys(cardRefs.current).forEach((cardId) => {
            if (!cardIds.has(cardId)) delete cardRefs.current[cardId];
        });
        Object.keys(cardNodeRefs.current).forEach((cardId) => {
            if (!cardIds.has(cardId)) delete cardNodeRefs.current[cardId];
        });
    }, [assetCards]);

    // Drop move entries for images that were removed; moves pointing at a card
    // that no longer exists are ignored by applyAttachmentMoves, so the image
    // simply falls back to its detected group.
    useEffect(() => {
        const attachmentIds = new Set(attachments.map((attachment) => attachment.id));
        setAttachmentMoves((current) => {
            const entries = Object.entries(current).filter(([attachmentId]) => attachmentIds.has(attachmentId));
            return entries.length === Object.keys(current).length ? current : Object.fromEntries(entries);
        });
    }, [attachments]);

    // Runs after the assets view is actually on screen, so the target card can
    // be measured and scrolled to.
    useEffect(() => {
        if (!pendingScrollCardId || activeView !== 'assets') return;
        cardNodeRefs.current[pendingScrollCardId]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setPendingScrollCardId(null);
    }, [pendingScrollCardId, activeView]);

    const moveAttachmentToCard = useCallback((attachmentId, targetCardId) => {
        setAttachmentMoves((current) => (
            current[attachmentId] === targetCardId ? current : { ...current, [attachmentId]: targetCardId }
        ));
    }, []);

    const handleStatusChange = useCallback((cardId, status) => {
        setCardStatuses((current) => ({ ...current, [cardId]: status }));
    }, []);

    const mediaAttachments = useMemo(() => getMediaViewAttachments(attachments), [attachments]);
    const previewAttachment = (attachmentId) => {
        const index = mediaAttachments.findIndex((attachment) => attachment.id === attachmentId);
        dispatch(openMediaViewModal({
            attachments: mediaAttachments,
            openedMediaIndex: Math.max(index, 0),
            showSenderDetail: false,
            isTicketChat: isChat,
        }));
    };

    // The uploader strip sits outside every card's form, so a value used from a
    // tile there is handed to the card that currently owns that image.
    const applyOcrValueToCard = useCallback((ocrField, attachment) => {
        const cardIndex = assetIndexByAttachmentId.get(attachment?.id);
        const cardId = cardIndex === undefined ? undefined : assetCards[cardIndex]?.id;
        if (!cardId) return null;
        return cardRefs.current[cardId]?.applyOcrField(ocrField, attachment) || null;
    }, [assetIndexByAttachmentId, assetCards]);

    const addManualCard = () => {
        if (isSaving) return;
        const cardId = createId('manual-asset');
        setManualCardIds((current) => [...current, cardId]);
        setPendingScrollCardId(cardId);
    };

    const removeCard = (card) => {
        if (isSaving) return;
        setManualCardIds((current) => current.filter((cardId) => cardId !== card.id));
        if (card.attachments.length > 0) removeAttachments(card.attachments.map((attachment) => attachment.id));
    };

    const collectCardValues = () => Object.fromEntries(
        assetCards
            .map((card) => [card.id, cardRefs.current[card.id]?.getFormValues()])
            .filter(([, values]) => values)
    );

    const applyOwnerToAll = useCallback((sourceCardId, owner) => {
        Object.entries(cardRefs.current).forEach(([cardId, instance]) => {
            if (cardId !== sourceCardId) instance?.setOwner(owner);
        });
        dispatch(sendToastMessage({ status: 'success', message: 'Owner applied to every detected asset.' }));
    }, [dispatch]);

    const updateSavedAsset = useCallback(({ assetKey, responseData, attachments: addedAttachments = [], assetPatch = {} }) => {
        const responseAsset = responseData?.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)
            ? responseData.data
            : {};
        setSavedAssets((current) => current.map((asset, index) => {
            if (getSavedAssetIdentity(asset, index) !== assetKey) return asset;
            const responseAttachments = responseAsset.medias || responseAsset.attachments;
            const mergedAttachments = [
                ...getSavedAssetAttachments(asset),
                ...(Array.isArray(responseAttachments) ? responseAttachments : []),
                ...addedAttachments,
            ].filter((attachment, attachmentIndex, allAttachments) => {
                const fileUrl = attachment?.file_url || attachment?.url;
                return fileUrl && allAttachments.findIndex((item) => (item?.file_url || item?.url) === fileUrl) === attachmentIndex;
            });
            return {
                ...asset,
                ...assetPatch,
                ...responseAsset,
                medias: mergedAttachments,
            };
        }));
    }, []);

    // Images added from inside a card belong to that card: they are pinned to it
    // before their OCR result can group them elsewhere, so the reading fills the
    // card the user uploaded into instead of opening another one.
    const addFilesToCard = (cardId, files) => {
        if (isSaving) return;
        const attachmentIds = addFiles(files);
        if (attachmentIds.length === 0) return;
        setAttachmentMoves((current) => ({
            ...current,
            ...Object.fromEntries(attachmentIds.map((attachmentId) => [attachmentId, cardId])),
        }));
    };

    const buildTransferState = () => ({
        attachments: getTransferAttachments(),
        cardValues: collectCardValues(),
        manualCardIds,
        attachmentMoves,
    });

    const canExpand = () => {
        if (isSaving) return false;
        if (isProcessing) {
            dispatch(sendToastMessage({ status: 'danger', message: 'Wait for all image uploads and OCR extraction to finish before expanding.' }));
            return false;
        }
        return true;
    };

    // The full-screen modal renders below the custom one, so the modal this form
    // is running in has to be dismissed before the full-screen view is opened.
    const openFullScreenForm = () => {
        if (!canExpand()) return;
        const modalComponent = (
            <PavAssetVerification
                ticket={ticket}
                milestone={milestone}
                variant='fullscreen'
                onSave={onSave}
                initialUpload={buildTransferState()}
                reviewSavedAssetsOnly={reviewSavedAssetsOnly}
                onSignOffFormBack={onSignOffFormBack}
            />
        );
        dispatch(closeModalAction());
        dispatch(openFullscreenModal({ heading: `${getPavMilestoneLabel(milestone)}${ticketNumber ? `: ${ticketNumber}` : ''}`, modalComponent }));
    };

    const shrinkToModal = () => {
        if (!canExpand()) return;
        const modalComponent = (
            <PavAssetVerification
                ticket={ticket}
                milestone={milestone}
                variant='modal'
                onSave={onSave}
                initialUpload={buildTransferState()}
                reviewSavedAssetsOnly={reviewSavedAssetsOnly}
                onSignOffFormBack={onSignOffFormBack}
            />
        );
        dispatch(closeFullScreenModalAction());
        dispatch(openCustomModal({
            heading: (
                <span className='text-black font-semibold'>
                    {getPavMilestoneLabel(milestone)}
                    {ticketNumber ? <span className='text-scogobgsky font-semibold'>: {ticketNumber}</span> : ''}
                </span>
            ),
            modalWidth: 'min(82rem, 95vw)',
            modalHeight: 'auto',
            maxHeight: '78vh',
            modalComponent,
        }));
    };

    const openExpandedForm = () => {
        if (!canExpand()) return;
        dispatch(openCustomModal({
            heading: (
                <span className='text-black font-semibold'>
                    {getPavMilestoneLabel(milestone)}
                    {ticketNumber ? <span className='text-scogobgsky font-semibold'>: {ticketNumber}</span> : ''}
                </span>
            ),
            modalWidth: 'min(82rem, 95vw)',
            modalHeight: 'auto',
            maxHeight: '78vh',
            modalComponent: (
                <PavAssetVerification
                    ticket={ticket}
                    milestone={milestone}
                    variant='modal'
                    onSave={onSave}
                    initialUpload={buildTransferState()}
                    reviewSavedAssetsOnly={reviewSavedAssetsOnly}
                    onSignOffFormBack={onSignOffFormBack}
                />
            ),
        }));
    };

    const commitSave = async () => {
        if (isSaving || assetCards.length === 0) return;

        const results = [];
        const assetTypeLabels = [];
        for (let i = 0; i < assetCards.length; i += 1) {
            const card = assetCards[i];
            if (card.attachments.length === 0) {
                dispatch(sendToastMessage({
                    status: 'danger',
                    message: `Photo is required for Asset ${i + 1}. Please add at least one image before saving.`,
                }));
                setActiveView('assets');
                cardRefs.current[card.id]?.expand?.();
                setPendingScrollCardId(card.id);
                return;
            }
            const result = await cardRefs.current[card.id]?.validate();
            if (!result?.valid) {
                const message = !result?.hasAttachments
                    ? `Photo is required for Asset ${i + 1}. Please add at least one image before saving.`
                    : 'Complete the required details for every asset before saving.';
                dispatch(sendToastMessage({ status: 'danger', message }));
                setActiveView('assets');
                cardRefs.current[card.id]?.expand?.();
                setPendingScrollCardId(card.id);
                return;
            }
            results.push(result.data);
            assetTypeLabels.push(result.assetTypeLabel);
        }
        const savedCards = assetCards.slice();

        const payloads = results.map((asset) => buildPavAssetPayload({
            asset,
            ticketId,
            milestoneId: milestone?.id,
        }));
        const pavFormData = {
            ticket_id: ticketId,
            milestone_id: milestone?.id,
            ...(payloads[0]?.owner ? { owner: payloads[0].owner } : {}),
            ...(results[0]?.contact_email ? { contact_email: results[0].contact_email } : {}),
            asset_type_id: results[0].asset_type_id,
            attachments: results[0].attachments,
            additional_fields: results[0].additional_fields,
            assets: results,
        };

        let savedCount = 0;
        let apiSuccessMessage = '';
        setIsSaving(true);
        try {
            for (let index = 0; index < payloads.length; index += 1) {
                const responseData = await new Promise((resolve, reject) => {
                    dispatch(addBoqInServiceRequest({
                        formName: `pav_add_asset_${milestone?.id}_${index}`,
                        payload: payloads[index],
                        onSuccess: resolve,
                        onError: reject,
                        refresh: false,
                        showToast: false,
                        pavFlow: true,
                    }));
                });
                if (typeof responseData?.message === 'string' && responseData.message.trim()) {
                    apiSuccessMessage = responseData.message;
                }
                const responseAsset = responseData?.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)
                    ? responseData.data
                    : {};
                const savedAsset = {
                    ...results[index],
                    ...responseAsset,
                    __pavLocalId: responseAsset.id || createId('saved-asset'),
                    milestone_id: responseAsset.milestone_id || milestone?.id,
                    asset_type_id: responseAsset.asset_type_id || results[index].asset_type_id,
                    asset_type_name: responseAsset.asset_type_name || assetTypeLabels[index],
                    serial_number: responseAsset.serial_number || payloads[index].serial_number,
                    additional_fields: responseAsset.additional_fields || results[index].additional_fields,
                    attachments: responseAsset.attachments || payloads[index].attachments,
                    ...(responseAsset.owner || payloads[index].owner
                        ? { owner: responseAsset.owner || payloads[index].owner }
                        : {}),
                };
                setSavedAssets((current) => mergeSavedAssets(current, [savedAsset]));
                savedCount += 1;
            }

            setHasSavedMilestoneAsset(true);
            dispatch(getServiceRequestById({ ticketId, callDetail: true }));
            if (typeof onSave === 'function') onSave(pavFormData);
            dispatch(sendToastMessage({
                status: 'success',
                message: apiSuccessMessage || `${savedCount} asset${savedCount === 1 ? '' : 's'} added successfully.`,
            }));
            removeAttachments(attachments.map((attachment) => attachment.id));
            setManualCardIds([]);
            setAttachmentMoves({});
            setCardStatuses({});
            cardRefs.current = {};
        } catch (error) {
            if (savedCount > 0) {
                setHasSavedMilestoneAsset(true);
                dispatch(getServiceRequestById({ ticketId, callDetail: true }));
                const savedSoFar = savedCards.slice(0, savedCount);
                const savedCardIds = new Set(savedSoFar.map((card) => card.id));
                setManualCardIds((current) => current.filter((cardId) => !savedCardIds.has(cardId)));
                const savedAttachmentIds = savedSoFar.flatMap((card) => card.attachments.map((attachment) => attachment.id));
                if (savedAttachmentIds.length > 0) removeAttachments(savedAttachmentIds);
            }
            dispatch(sendToastMessage({
                status: 'danger',
                message: `${savedCount > 0 ? `${savedCount} asset${savedCount === 1 ? '' : 's'} saved. ` : ''}${error?.response?.data?.messages?.[0] || error?.message || 'Unable to add the asset.'}`,
            }));
        } finally {
            setActiveView('assets');
            setIsSaving(false);
        }
    };

    const statuses = assetCards.map((card) => cardStatuses[card.id]);
    const missingImageCard = assetCards.find((card) => card.attachments.length === 0);
    const readyCount = statuses.filter((status) => status?.isValid && status?.hasAttachments).length;
    const hasInvalidCard = statuses.length !== assetCards.length || statuses.some((status) => !status?.isValid || !status?.hasAttachments);
    const footerNeedsAttention = assetCards.length === 0 || hasInvalidCard || hasFailedAttachment || Boolean(missingImageCard);
    const isSaveDisabled = isSaving || isProcessing || assetCards.length === 0 || hasInvalidCard || Boolean(missingImageCard);
    const showFooterMessage = !(isCompact && assetCards.length === 0 && !isProcessing);
    const footerMessage = isProcessing
        ? 'Reading your images to detect assets...'
        : assetCards.length === 0
            ? 'Upload asset images to begin'
            : missingImageCard
                ? `Photo is required for Asset ${assetCards.indexOf(missingImageCard) + 1}. Add an image to continue.`
                : hasInvalidCard
                    ? 'Complete the required details for every detected asset.'
                    : hasFailedAttachment
                        ? 'Some images could not be read; their details can be entered manually.'
                        : 'All assets are ready to save.';

    const handleFooterMessageClick = () => {
        if (missingImageCard) {
            setActiveView('assets');
            cardRefs.current[missingImageCard.id]?.expand?.();
            setPendingScrollCardId(missingImageCard.id);
        } else if (hasInvalidCard) {
            const firstInvalidCard = assetCards.find((card) => !cardStatuses[card.id]?.isValid || card.attachments.length === 0);
            if (firstInvalidCard) {
                setActiveView('assets');
                cardRefs.current[firstInvalidCard.id]?.expand?.();
                setPendingScrollCardId(firstInvalidCard.id);
            }
        }
    };

    return (
        <div className={`flex min-h-0 flex-col overflow-hidden bg-white ${isCompact ? 'max-h-[40vh]' : isChat ? 'h-[40vh]' : isFullScreen ? 'h-full' : 'max-h-[78vh] rounded-xl shadow-sm'}`}>
            {isFullScreen && activeView !== 'raise_signoff' && (
                <FullScreenHeader milestone={currentMilestone} ticketNumber={ticketNumber} onClose={() => dispatch(closeFullScreenModalAction())} onShrink={shrinkToModal} />
            )}
            {isChat && activeView !== 'raise_signoff' && (
                <ChatHeader
                    savedCount={savedAssets.length}
                    assetCount={assetCards.length}
                    isSaving={isSaving}
                    isViewingSaved={activeView === 'saved'}
                    onExpand={openExpandedForm}
                    onViewSaved={() => setActiveView((current) => current === 'saved' ? 'assets' : 'saved')}
                    onAddAsset={() => {
                        setActiveView('assets');
                        addManualCard();
                    }}
                />
            )}
            <div className={`${activeView === 'assets' ? 'flex' : 'hidden'} min-h-0 flex-col overflow-hidden bg-white ${isChat || isFullScreen ? 'flex-1' : 'max-h-[78vh]'}`}>
                {!isChat && (
                    <FlowSummary imageCount={attachments.length} assetCount={assetCards.length} readyCount={readyCount} isProcessing={isProcessing} savedCount={savedAssets.length} isSaving={isSaving} onViewSaved={() => setActiveView('saved')} onAddAsset={addManualCard} onExpand={isFullScreen ? undefined : openFullScreenForm} />
                )}

                <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto bg-scogof8 ${isChat ? 'p-2' : isFullScreen ? 'p-6' : 'max-h-[calc(78vh-10rem)] p-4'}`}>
                    <div className={`flex flex-1 flex-col ${assetCards.length > 0 ? 'space-y-3' : ''}`}>
                        <PavImageUploader
                            attachments={attachments}
                            assetIndexByAttachmentId={assetIndexByAttachmentId}
                            onUseOcrValue={applyOcrValueToCard}
                            assetCount={assetCards.length}
                            disabled={isSaving}
                            isProcessing={isProcessing}
                            processedCount={processedCount}
                            onAddFiles={addFiles}
                            onRemoveAttachment={removeAttachment}
                            onPreviewAttachment={previewAttachment}
                            onAddAsset={addManualCard}
                            isCompact={isCompact}
                            isFullScreen={isFullScreen}
                        />

                        {assetCards.length > 0 && (
                            <section>
                                <div className='grid grid-cols-1 items-start gap-2'>
                                    {assetCards.map((card, index) => (
                                        <PavAssetCard
                                            key={card.id}
                                            ref={(instance) => {
                                                if (instance) cardRefs.current[card.id] = instance;
                                                else delete cardRefs.current[card.id];
                                            }}
                                            containerRef={(node) => {
                                                if (node) cardNodeRefs.current[card.id] = node;
                                                else delete cardNodeRefs.current[card.id];
                                            }}
                                            group={card}
                                            index={index}
                                            ticket={ticket}
                                            assetTypes={assetTypes}
                                            initialValues={initialUpload?.cardValues?.[card.id]}
                                            isCompact={isCompact}
                                            isFullScreen={isFullScreen}
                                            shouldFocus={pendingScrollCardId === card.id || firstEmptyAssetCardId === card.id}
                                            canApplyToAll={assetCards.length > 1}
                                            onStatusChange={handleStatusChange}
                                            onRemoveGroup={() => removeCard(card)}
                                            onPreviewAttachment={previewAttachment}
                                            onAddFiles={(files) => addFilesToCard(card.id, files)}
                                            onApplyOwnerToAll={applyOwnerToAll}
                                            onMoveAttachment={isSaving ? undefined : moveAttachmentToCard}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>
                </div>

                <div className='shrink-0 border-t border-slate-200 bg-white shadow-[0_-4px_14px_rgba(15,23,42,0.05)]'>
                    <div className={`flex flex-wrap items-center justify-between ${isChat ? `gap-2 px-3 ${showFooterMessage ? 'py-2' : 'py-1.5'}` : 'gap-3 px-4 py-3'}`}>
                        {showFooterMessage && (
                            <div
                                className={`flex min-w-0 items-center gap-2 ${(missingImageCard || hasInvalidCard) ? 'cursor-pointer transition-opacity hover:opacity-80' : ''}`}
                                onClick={(missingImageCard || hasInvalidCard) ? handleFooterMessageClick : undefined}
                                title={(missingImageCard || hasInvalidCard) ? 'Click to view this asset' : undefined}
                            >
                                <span className={`material-icons text-font16 ${isProcessing ? 'animate-pulse text-scogobgsky' : missingImageCard ? 'text-red-500' : footerNeedsAttention ? 'text-amber-600' : 'text-emerald-600'}`}>
                                    {isProcessing ? 'document_scanner' : missingImageCard ? 'error_outline' : footerNeedsAttention ? 'info' : 'check_circle'}
                                </span>
                                <p className={`hidden font-normal leading-5 text-slate-600 sm:block ${isChat ? 'text-font10' : 'text-font11'} ${missingImageCard ? '!font-medium !text-red-700' : hasFailedAttachment && !isProcessing ? '!text-amber-800' : ''}`}>
                                    {footerMessage}
                                </p>
                            </div>
                        )}
                        <div className='ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2'>
                            <PavMilestoneActions ticket={currentTicket} milestone={currentMilestone} compact={isCompact} hasSavedMilestoneAsset={hasSavedMilestoneAsset} onRaiseSignOff={() => setActiveView('raise_signoff')} />
                            <button type='button' onClick={commitSave} disabled={isSaveDisabled} className={`flex shrink-0 items-center justify-center gap-2 rounded-lg bg-scogoprimary font-semibold text-white shadow-sm transition-colors ${isCompact ? 'h-8 px-3 text-font11' : 'h-10 min-w-[11rem] px-5 text-font12'} ${isSaveDisabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-slate-800'}`}>
                                {isSaving ? <Loader size='16' thickness='2' color='#ffffff' /> : <span className='material-icons text-base'>save</span>}
                                {isSaving ? 'Saving assets...' : assetCards.length > 1 ? `Save ${assetCards.length} assets` : 'Save'}
                            </button>
                        </div>
                    </div>
                    {footerContent && <div className={isChat ? 'px-3 pb-2' : 'px-4 pb-3'}>{footerContent}</div>}
                </div>
            </div>
            {activeView === 'saved' && (
                <SavedPavAssets
                    assets={savedAssets}
                    assetTypes={assetTypes}
                    orgId={orgId}
                    isChat={isChat}
                    isCompact={isCompact}
                    ticketId={ticketId}
                    milestoneId={milestone?.id}
                    onBack={reviewSavedAssetsOnly ? undefined : () => setActiveView('assets')}
                    onAssetUpdated={updateSavedAsset}
                    readOnly={reviewSavedAssetsOnly}
                />
            )}
            {activeView === 'saved' && reviewSavedAssetsOnly && (
                <div className='shrink-0 border-t border-slate-200 bg-white px-4 py-3 shadow-[0_-4px_14px_rgba(15,23,42,0.05)]'>
                    <div className='flex flex-wrap items-center justify-end gap-2'>
                        <PavMilestoneActions ticket={currentTicket} milestone={currentMilestone} hasSavedMilestoneAsset={hasSavedMilestoneAsset} onSignOffFormBack={onSignOffFormBack} />
                    </div>
                </div>
            )}
            {activeView === 'raise_signoff' && (
                <PavRaiseSignOffView
                    ticket={currentTicket}
                    milestone={currentMilestone}
                    isChat={isChat}
                    onBack={() => setActiveView('assets')}
                />
            )}
        </div>
    );
}
